<script lang="ts" module>
  import type { Habit, HabitCategory } from '../../lib/types';

  export interface HabitsTodayGroup {
    id: string;
    /** Null for habits without a category. */
    category: HabitCategory | null;
    habits: Habit[];
    completed: number;
    total: number;
  }
</script>

<script lang="ts">
  /**
   * HabitsToday - the phone check-in view of Habits (plans/mobile-v2.md ma-twon):
   * habits grouped by category, one row each with a last-7-days sparkline, the
   * streak, and a 48×48 target. Tap cycles blank → completed → blank;
   * long-press (or the ⋯ menu) marks the day missed, like a Week grid cell.
   *
   * Presentational: the page owns the data and the API calls, and passes only
   * the habits scheduled on `date`.
   */
  import Icon from '../Icon.svelte';
  import {
    formatStreak,
    habitSparkline,
    habitStreak,
    type HabitStatusLookup,
  } from '../../lib/habits';
  import { createLongPressTracker } from '../../lib/longPress';
  import { onDestroy } from 'svelte';

  interface Props {
    groups: HabitsTodayGroup[];
    /** The day being checked in, `YYYY-MM-DD`. */
    date: string;
    statusFor: HabitStatusLookup;
    /** First date whose logs are loaded: a streak reaching it may be longer. */
    oldestLoaded: string;
    /** Fill completed days with the category colour (else `fallbackColor`). */
    useCategoryColor: boolean;
    fallbackColor: string;
    onToggleDone: (habit: Habit) => void;
    onToggleMissed: (habit: Habit) => void;
    onMore: (habit: Habit) => void;
  }

  let {
    groups,
    date,
    statusFor,
    oldestLoaded,
    useCategoryColor,
    fallbackColor,
    onToggleDone,
    onToggleMissed,
    onMore,
  }: Props = $props();

  const UNCATEGORIZED_COLOR = '#94a3b8';
  const longPress = createLongPressTracker();
  onDestroy(longPress.destroy);

  function completedColor(category: HabitCategory | null) {
    return useCategoryColor ? (category?.color ?? fallbackColor) : fallbackColor;
  }

  function sparklineLabel(days: ReturnType<typeof habitSparkline>) {
    const done = days.filter((day) => day.status === 'completed').length;
    const missed = days.filter((day) => day.status === 'missed').length;
    return `Last ${days.length} days: ${done} done${missed ? `, ${missed} missed` : ''}`;
  }

  function targetKeydown(event: KeyboardEvent, habit: Habit) {
    if (event.key.toLowerCase() !== 'm') return;
    event.preventDefault();
    onToggleMissed(habit);
  }
</script>

<div class="today">
  {#each groups as group (group.id)}
    {@const color = group.category?.color ?? UNCATEGORIZED_COLOR}
    <section class="group" aria-label={group.category?.name ?? 'Uncategorized'}>
      <header class="cat" style={`--dot: ${color}`}>
        <span class="dot" aria-hidden="true"></span>
        <h2 class="name">{group.category?.name ?? 'Uncategorized'}</h2>
        {#if group.category?.tagline}<span class="tagline">{group.category.tagline}</span>{/if}
        <span class="ratio" aria-label={`${group.completed} of ${group.total} done`}
          >{group.completed}/{group.total}</span
        >
      </header>
      <ul class="list" style={`--c: ${completedColor(group.category)}`}>
        {#each group.habits as habit (habit.id)}
          {@const status = statusFor(habit, date)}
          {@const days = habitSparkline(habit, statusFor, date)}
          {@const streak = habitStreak(habit, statusFor, date, oldestLoaded)}
          <li class="row" class:archived={!!habit.archivedAt}>
            <button
              type="button"
              class="more"
              aria-label={`Actions for ${habit.title}`}
              onclick={() => onMore(habit)}><Icon name="more" size={18} /></button
            >
            <div class="main">
              <div class="title">{habit.title}</div>
              <div class="trail">
                <span class="spark" role="img" aria-label={sparklineLabel(days)}>
                  {#each days as day (day.date)}
                    <i
                      class:done={day.status === 'completed'}
                      class:miss={day.status === 'missed'}
                      class:off={!day.scheduled}
                    ></i>
                  {/each}
                </span>
                {#if streak.count >= 2}
                  <span class="streak" title={`${formatStreak(streak)} day streak`}
                    ><span aria-hidden="true">🔥</span>
                    {formatStreak(streak)}<span class="sr-only"> day streak</span></span
                  >
                {/if}
                {#if habit.archivedAt}<span class="tag">Archived</span>{/if}
              </div>
            </div>
            <button
              type="button"
              class="target"
              class:completed={status === 'completed'}
              class:missed={status === 'missed'}
              aria-label={`${habit.title}, ${date}: ${status || 'unlogged'}`}
              aria-keyshortcuts="M"
              onclick={() => {
                if (!longPress.consumeClick(habit.id)) onToggleDone(habit);
              }}
              onpointerdown={(event) =>
                longPress.pointerDown(event, habit.id, () => onToggleMissed(habit))}
              onpointermove={longPress.pointerMove}
              onpointerup={longPress.pointerEnd}
              onpointercancel={longPress.pointerEnd}
              onpointerleave={longPress.pointerEnd}
              onkeydown={(event) => targetKeydown(event, habit)}
              oncontextmenu={(event) =>
                longPress.contextMenu(event, habit.id, () => onToggleMissed(habit))}
            >
              {#if status === 'completed'}
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="m5 12.5 4.5 4.5L19 7.5" />
                </svg>
              {:else if status === 'missed'}
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="m7 7 10 10M17 7 7 17" />
                </svg>
              {:else}
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="m5 12.5 4.5 4.5L19 7.5" />
                </svg>
              {/if}
            </button>
          </li>
        {/each}
      </ul>
    </section>
  {/each}
</div>

<style>
  .today {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .cat {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 4px 6px;
  }

  .dot {
    flex: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--dot);
  }

  .name {
    margin: 0;
    color: #111827;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
  }

  .tagline {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    color: #6b7280;
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ratio {
    margin-left: auto;
    flex: none;
    color: #6b7280;
    font-size: 13px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .tagline + .ratio {
    margin-left: 0;
  }

  .list {
    margin: 0;
    padding: 0;
    overflow: hidden;
    list-style: none;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 64px;
    padding: 8px 12px 8px 0;
    border-top: 1px solid #f1f5f9;
  }

  .row:first-child {
    border-top: 0;
  }

  .more {
    position: relative;
    flex: none;
    display: grid;
    place-items: center;
    width: 40px;
    height: var(--mobile-tap, 44px);
    padding: 0;
    color: #94a3b8;
    background: none;
    border: 0;
    border-radius: 8px;
    -webkit-tap-highlight-color: transparent;
  }

  .more::after {
    content: '';
    position: absolute;
    inset: 0 -4px 0 0;
  }

  .more:active {
    background: #f1f5f9;
  }

  .main {
    flex: 1;
    min-width: 0;
    padding-left: 4px;
  }

  .title {
    display: -webkit-box;
    overflow: hidden;
    color: #172033;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow-wrap: anywhere;
  }

  .archived .title {
    color: #6b7280;
  }

  .trail {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 5px;
  }

  .spark {
    display: flex;
    flex: none;
    gap: 3px;
  }

  .spark i {
    width: 14px;
    height: 6px;
    background: #e5e7eb;
    border-radius: 2px;
  }

  .spark i.off {
    background: #f3f4f6;
  }

  .spark i.done {
    background: var(--c);
  }

  .spark i.miss {
    background: #fca5a5;
  }

  .streak {
    color: #6b7280;
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .tag {
    padding: 0 6px;
    color: #6b7280;
    background: #f3f4f6;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  /* 48×48 check-in target. */
  .target {
    flex: none;
    display: grid;
    place-items: center;
    width: 48px;
    height: 48px;
    padding: 0;
    color: #cbd5e1;
    background: none;
    border: 2px dashed #cbd5e1;
    border-radius: 14px;
    touch-action: manipulation;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: transparent;
  }

  .target svg {
    width: 24px;
    height: 24px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.6;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .target:active {
    transform: scale(0.94);
  }

  .target.completed {
    color: white;
    background: var(--c);
    border-color: transparent;
  }

  .target.missed {
    color: #dc2626;
    background: #fee2e2;
    border-color: transparent;
  }
</style>
